// Connector Catalog (admin-portal) — the PLATFORM-global registry of integration
// connector TYPES (definitions), owned by migration-service. Distinct from a tenant's
// connector *instances* (connections). Platform operators curate which connector types
// exist and their lifecycle status (available → offered to tenants; beta → offered with
// a flag; internal → designed, not yet offered). Reached through the gateway at
// /api/v1/integrations/admin/catalog/* (platform-role-guarded server-side).
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/integrations/admin/catalog`;

export type ConnectorStatus = 'available' | 'beta' | 'internal';
export type ConnectorDirection = 'inbound' | 'outbound' | 'bidirectional';
export type ConnectorTransport = 'csv' | 'sftp' | 'rest' | 'webhook';

export interface ConnectorDefinition {
  id: string;
  key: string;
  name: string;
  category: string;
  direction: ConnectorDirection;
  transport: ConnectorTransport;
  config_schema: Record<string, unknown>;
  credential_schema: Record<string, unknown>;
  target_key?: string;
  status: ConnectorStatus;
  docs_url?: string;
  /** Platform-curated certification badge (marketplace). */
  verified?: boolean;
  verified_at?: string;
}

// Upsert payload — key identifies the definition (create if new, update if existing).
export type ConnectorInput = Omit<ConnectorDefinition, 'id'>;

export const listDefinitions = () =>
  axios.get<{ connectors: ConnectorDefinition[] }>(BASE).then((r) => r.data.connectors ?? []);

export const saveDefinition = (d: ConnectorInput) =>
  axios.post<ConnectorDefinition>(BASE, d).then((r) => r.data);

export const setDefinitionStatus = (key: string, status: ConnectorStatus) =>
  axios.patch<ConnectorDefinition>(`${BASE}/${encodeURIComponent(key)}/status`, { status }).then((r) => r.data);

export const deleteDefinition = (key: string) =>
  axios.delete(`${BASE}/${encodeURIComponent(key)}`).then((r) => r.data);

/** Flip the certification (verified) badge on a connector. */
export const setDefinitionVerified = (key: string, verified: boolean) =>
  axios.post<ConnectorDefinition>(`${BASE}/${encodeURIComponent(key)}/verified`, { verified }).then((r) => r.data);

export interface CertifyCheck { name: string; ok: boolean; detail?: string }
export interface CertifyReport { key: string; passed: boolean; checks: CertifyCheck[] }

/** Run the automated certification checks; awards the verified badge on a full pass. */
export const certifyDefinition = (key: string) =>
  axios.post<CertifyReport>(`${BASE}/${encodeURIComponent(key)}/certify`, {}).then((r) => r.data);
